/**
 * Error Monitoring Service (Sentry)
 *
 * Provides professional error tracking for production:
 * - Automatic JavaScript error capture
 * - Failed API call tracking
 * - Performance monitoring
 * - User context enrichment
 * - Alert configuration for error spikes
 *
 * @see https://docs.sentry.io/platforms/javascript/guides/react/
 */

import type { ErrorInfo } from 'react';
import * as Sentry from '@sentry/react';
import { createLogger } from './loggingService';

const logger = createLogger('ErrorMonitoringService');

// =============================================================================
// Configuration
// =============================================================================

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined;
const ENVIRONMENT = import.meta.env.MODE;
const RELEASE_VERSION = import.meta.env.VITE_RELEASE_VERSION as string | undefined;

// Error types that should be filtered (reduce noise)
const IGNORED_ERRORS = [
  // Browser extensions
  'ResizeObserver loop limit exceeded',
  'ResizeObserver loop completed with undelivered notifications',
  // Network errors that are expected
  'Failed to fetch',
  'Load failed',
  'NetworkError',
  // User aborted
  'AbortError',
  'The user aborted a request',
  // Third-party scripts
  'Script error',
  'Non-Error exception captured',
];

// URLs to ignore (third-party scripts, analytics, etc.)
const IGNORED_URLS = [
  /extensions\//i,
  /^chrome:\/\//i,
  /^moz-extension:\/\//i,
  /googletagmanager\.com/i,
  /google-analytics\.com/i,
];

let isInitialized = false;

// =============================================================================
// Initialization
// =============================================================================

/**
 * Initialize Sentry error monitoring
 * Should be called once at app startup (in main.tsx)
 */
export const initializeErrorMonitoring = (): void => {
  if (isInitialized) {
    logger.debug('Already initialized');
    return;
  }

  if (!SENTRY_DSN) {
    logger.debug('VITE_SENTRY_DSN not set, error monitoring disabled');
    return;
  }

  // Don't initialize in development unless explicitly enabled
  if (import.meta.env.DEV && !import.meta.env.VITE_ENABLE_SENTRY_DEV) {
    logger.debug('Disabled in development mode');
    return;
  }

  try {
    Sentry.init({
      dsn: SENTRY_DSN,
      environment: ENVIRONMENT,
      release: RELEASE_VERSION || `amazon-profit-studio@${ENVIRONMENT}`,

      // Performance monitoring
      tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0, // 10% in prod, 100% in dev
      tracePropagationTargets: [
        'localhost',
        /^https:\/\/.*\.vercel\.app/,
        /^https:\/\/amazonprofitstudio\.com/,
      ],

      // Session replay for debugging (disabled by default to save quota)
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: import.meta.env.PROD ? 0.1 : 0, // Capture 10% of errors with replay

      // Error filtering
      ignoreErrors: IGNORED_ERRORS,
      denyUrls: IGNORED_URLS,

      // Attach stack traces to all events
      attachStacktrace: true,

      // Max breadcrumbs to keep
      maxBreadcrumbs: 50,

      // Before sending, enrich or filter events
      beforeSend(event, hint) {
        // Don't send events in development
        if (import.meta.env.DEV && !import.meta.env.VITE_ENABLE_SENTRY_DEV) {
          return null;
        }

        // Filter out browser extension errors
        const frames = event.exception?.values?.[0]?.stacktrace?.frames;
        if (frames?.some(frame => frame.filename?.includes('extension://'))) {
          return null;
        }

        // Add custom context
        const error = hint.originalException as Error | undefined;
        if (error) {
          event.fingerprint = event.fingerprint || ['{{ default }}'];

          // Group similar errors together
          if (error.message) {
            // Normalize dynamic content in error messages
            const normalizedMessage = error.message
              .replace(/\d+/g, 'N') // Replace numbers
              .replace(/[a-f0-9-]{36}/gi, 'UUID') // Replace UUIDs
              .slice(0, 100); // Limit length

            event.fingerprint.push(normalizedMessage);
          }
        }

        return event;
      },

      // Configure breadcrumb collection
      beforeBreadcrumb(breadcrumb) {
        // Filter out noisy breadcrumbs
        if (breadcrumb.category === 'console' && breadcrumb.level === 'debug') {
          return null;
        }

        // Redact sensitive data from fetch breadcrumbs
        if (breadcrumb.category === 'fetch' && breadcrumb.data?.url) {
          const url = breadcrumb.data.url as string;
          if (url.includes('token') || url.includes('password')) {
            breadcrumb.data.url = '[REDACTED]';
          }
        }

        return breadcrumb;
      },

      // React-specific integrations
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          maskAllText: true,
          blockAllMedia: true,
        }),
      ],
    });

    isInitialized = true;
    logger.info('Initialized', { environment: ENVIRONMENT, release: RELEASE_VERSION });
  } catch (error) {
    logger.error('Failed to initialize', {
      error: error instanceof Error ? error.message : String(error),
    });
  }
};

// =============================================================================
// Error Capture
// =============================================================================

/**
 * Capture a JavaScript error with optional context
 */
export const captureError = (
  error: Error,
  context?: Record<string, unknown>
): string | undefined => {
  if (!isInitialized) {
    logger.debug('Not initialized, skipping error capture', { error: error.message });
    return undefined;
  }

  try {
    const eventId = Sentry.captureException(error, {
      extra: context,
    });

    logger.debug('Error captured', { eventId, error: error.message });
    return eventId;
  } catch (captureError) {
    logger.error('Failed to capture error', {
      originalError: error.message,
      captureError: captureError instanceof Error ? captureError.message : String(captureError),
    });
    return undefined;
  }
};

/**
 * Capture a message (non-error event)
 */
export const captureMessage = (
  message: string,
  level: Sentry.SeverityLevel = 'info',
  context?: Record<string, unknown>
): string | undefined => {
  if (!isInitialized) {
    return undefined;
  }

  try {
    const eventId = Sentry.captureMessage(message, {
      level,
      extra: context,
    });

    logger.debug('Message captured', { eventId, message, level });
    return eventId;
  } catch (error) {
    logger.error('Failed to capture message', {
      error: error instanceof Error ? error.message : String(error),
    });
    return undefined;
  }
};

// =============================================================================
// API Error Tracking
// =============================================================================

/**
 * Track a failed API call
 */
export const captureApiError = (
  url: string,
  method: string,
  status: number,
  errorMessage: string,
  requestData?: unknown
): string | undefined => {
  if (!isInitialized) {
    return undefined;
  }

  const error = new Error(`API Error: ${method} ${url} returned ${status}`);
  error.name = 'ApiError';

  return captureError(error, {
    api: {
      url: url.replace(/token=[^&]+/g, 'token=[REDACTED]'),
      method,
      status,
      errorMessage,
    },
    // Only include request data in development (may contain sensitive info)
    requestData: import.meta.env.DEV ? requestData : undefined,
  });
};

// =============================================================================
// Performance Monitoring
// =============================================================================

/**
 * Start a performance transaction for measuring operations
 * @returns A function to finish the transaction
 */
export const startTransaction = (
  name: string,
  operation: string,
  data?: Record<string, unknown>
): (() => void) => {
  if (!isInitialized) {
    return () => {}; // No-op if not initialized
  }

  const span = Sentry.startInactiveSpan({
    name,
    op: operation,
    attributes: data ? Object.fromEntries(
      Object.entries(data).map(([k, v]) => [k, String(v)])
    ) : undefined,
  });

  return () => {
    span?.end();
  };
};

/**
 * Measure async operation performance
 */
export const measurePerformance = async <T>(
  name: string,
  operation: string,
  fn: () => Promise<T>,
  data?: Record<string, unknown>
): Promise<T> => {
  const finish = startTransaction(name, operation, data);

  try {
    const result = await fn();
    finish();
    return result;
  } catch (error) {
    if (error instanceof Error) {
      captureError(error, { operation: name, ...data });
    }
    finish();
    throw error;
  }
};

// =============================================================================
// User Context
// =============================================================================

/**
 * Set user context for error tracking
 * Call this after user authentication
 */
export const setUserContext = (user: {
  id: string;
  email?: string;
  username?: string;
}): void => {
  if (!isInitialized) {
    return;
  }

  Sentry.setUser({
    id: user.id,
    email: user.email,
    username: user.username,
  });

  logger.debug('User context set', { userId: user.id });
};

/**
 * Clear user context
 * Call this on logout
 */
export const clearUserContext = (): void => {
  if (!isInitialized) {
    return;
  }

  Sentry.setUser(null);
  logger.debug('User context cleared');
};

// =============================================================================
// Breadcrumbs
// =============================================================================

/**
 * Add a custom breadcrumb for debugging context
 */
export const addBreadcrumb = (
  message: string,
  category: string,
  level: Sentry.SeverityLevel = 'info',
  data?: Record<string, unknown>
): void => {
  if (!isInitialized) {
    return;
  }

  Sentry.addBreadcrumb({
    message,
    category,
    level,
    data,
    timestamp: Date.now() / 1000,
  });
};

// =============================================================================
// Context and Tags
// =============================================================================

/**
 * Set a tag for filtering errors in Sentry dashboard
 */
export const setTag = (key: string, value: string): void => {
  if (!isInitialized) {
    return;
  }

  Sentry.setTag(key, value);
};

/**
 * Set extra context data
 */
export const setExtra = (key: string, value: unknown): void => {
  if (!isInitialized) {
    return;
  }

  Sentry.setExtra(key, value);
};

/**
 * Set the current feature/module context
 */
export const setFeatureContext = (feature: string): void => {
  setTag('feature', feature);
};

// =============================================================================
// Error Boundary Integration
// =============================================================================

/**
 * Capture error from React error boundary
 * Use this in ErrorBoundary.componentDidCatch
 */
export const captureErrorBoundaryError = (
  error: Error,
  errorInfo: ErrorInfo,
  feature: string
): string | undefined => {
  if (!isInitialized) {
    return undefined;
  }

  return Sentry.withScope((scope) => {
    scope.setTag('feature', feature);
    scope.setTag('error_boundary', 'true');
    scope.setExtra('componentStack', errorInfo.componentStack);

    return Sentry.captureException(error);
  });
};

// =============================================================================
// Alert Configuration (Documentation)
// =============================================================================

/**
 * Alert Rules (configure in Sentry Dashboard):
 *
 * 1. **Critical Error Spike**
 *    - Trigger: > 50 errors in 5 minutes
 *    - Action: Email + Slack notification
 *    - Tags: All environments
 *
 * 2. **API Error Rate**
 *    - Trigger: > 10% API errors in 15 minutes
 *    - Action: Email notification
 *    - Filter: error.type = 'ApiError'
 *
 * 3. **New Error Type**
 *    - Trigger: First seen issue
 *    - Action: Slack notification
 *    - Tags: Production only
 *
 * 4. **Performance Degradation**
 *    - Trigger: p95 latency > 3s
 *    - Action: Email notification
 *    - Filter: transaction.op = 'analysis'
 *
 * Configure these in: https://sentry.io/settings/[org]/projects/[project]/alerts/
 */

// =============================================================================
// Utility Exports
// =============================================================================

/**
 * Check if error monitoring is enabled
 */
export const isErrorMonitoringEnabled = (): boolean => {
  return isInitialized;
};

/**
 * Get the Sentry SDK for advanced usage
 * Use sparingly - prefer the wrapper functions above
 */
export const getSentry = (): typeof Sentry | null => {
  if (!isInitialized) {
    return null;
  }
  return Sentry;
};

/**
 * Flush pending events (call before page unload if needed)
 */
export const flush = async (timeout = 2000): Promise<boolean> => {
  if (!isInitialized) {
    return true;
  }
  return Sentry.flush(timeout);
};
